const MM_TO_PT = 72 / 25.4;
export const THERMAL_WIDTH_PT = Math.round((80 * 72 / 25.4) * 100) / 100;
export const THERMAL_PAPER_WIDTH_MM = 80;
export const THERMAL_LEFT_MARGIN_MM = 3.53;
export const THERMAL_RIGHT_MARGIN_MM = 3.53;

const round = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
export const ptToMm = (value) => round(Number(value || 0) / MM_TO_PT);
export const mmToPt = (value) => round(Number(value || 0) * MM_TO_PT);

export const POS_ITEMS_DEFAULT_COLUMNS = [
  { field: 'description', label: 'Desc', widthMm: 34, align: 'left' },
  { field: 'unit_price', label: 'Price', widthMm: 12.5, align: 'right' },
  { field: 'qty', label: 'Qt', widthMm: 8, align: 'center' },
  { field: 'line_total', label: 'Valor', widthMm: 18, align: 'right' },
];

export const POS_TAX_DEFAULT_COLUMNS = [
  { field: 'rate', label: 'Taxa', widthMm: 10, align: 'center' },
  { field: 'base', label: 'Base', widthMm: 21.5, align: 'right' },
  { field: 'vat', label: 'IVA', widthMm: 15.5, align: 'right' },
  { field: 'total', label: 'Total', widthMm: 20, align: 'right' },
];

const POS_SECTION_LIMITS = {
  items: {
    minX: THERMAL_LEFT_MARGIN_MM,
    maxX: 8,
    minTableHeight: 24,
    maxTableHeight: 72,
    minFontSize: 4.8,
    maxFontSize: 7.2,
    minTitleGap: 3,
    minColumnWidths: {
      description: 24,
      unit_price: 10,
      qty: 6,
      line_total: 14,
    },
  },
  tax: {
    minX: THERMAL_LEFT_MARGIN_MM,
    maxX: 10,
    minTableHeight: 12,
    maxTableHeight: 32,
    minFontSize: 5,
    maxFontSize: 7,
    minTitleGap: 3,
    minColumnWidths: {
      rate: 8,
      base: 16,
      vat: 11,
      total: 13,
    },
  },
};

const sumWidthsPt = (columns = []) => round(columns.reduce((total, column) => total + mmToPt(column.widthMm || 0), 0));

const clamp = (value, min, max) => Math.min(Math.max(Number(value || 0), min), max);

const getSectionLimits = (sectionKey = 'items') => POS_SECTION_LIMITS[sectionKey] || POS_SECTION_LIMITS.items;

export const getPosSectionMaxWidthMm = (sectionKey = 'items', xMm = THERMAL_LEFT_MARGIN_MM) => {
  const limits = getSectionLimits(sectionKey);
  const safeX = clamp(xMm, limits.minX, limits.maxX);
  return round(Math.max(40, THERMAL_PAPER_WIDTH_MM - safeX - THERMAL_RIGHT_MARGIN_MM));
};

const normalizeColumnWidths = (columns = [], maxWidthMm, minColumnWidths = {}) => {
  const nextColumns = (columns || []).map((column) => ({
    ...column,
    widthMm: round(Math.max(Number(column.widthMm || 0), Number(minColumnWidths[column.field] || 3))),
  }));

  let totalWidth = nextColumns.reduce((total, column) => total + Number(column.widthMm || 0), 0);
  if (totalWidth <= maxWidthMm) {
    return nextColumns;
  }

  for (let iteration = 0; iteration < 6 && totalWidth > maxWidthMm; iteration += 1) {
    const overflow = totalWidth - maxWidthMm;
    const adjustableColumns = nextColumns
      .map((column, index) => ({
        index,
        reducible: Number(column.widthMm || 0) - Number(minColumnWidths[column.field] || 3),
      }))
      .filter((column) => column.reducible > 0);

    if (adjustableColumns.length === 0) break;

    const totalReducible = adjustableColumns.reduce((sum, column) => sum + column.reducible, 0);
    adjustableColumns.forEach(({ index, reducible }) => {
      const proportionalReduction = overflow * (reducible / totalReducible);
      const currentWidth = Number(nextColumns[index].widthMm || 0);
      const minWidth = Number(minColumnWidths[nextColumns[index].field] || 3);
      nextColumns[index].widthMm = round(Math.max(minWidth, currentWidth - proportionalReduction));
    });

    totalWidth = nextColumns.reduce((total, column) => total + Number(column.widthMm || 0), 0);
  }

  if (totalWidth > maxWidthMm && nextColumns.length > 0) {
    const lastIndex = nextColumns.length - 1;
    const minWidth = Number(minColumnWidths[nextColumns[lastIndex].field] || 3);
    nextColumns[lastIndex].widthMm = round(Math.max(minWidth, Number(nextColumns[lastIndex].widthMm || 0) - (totalWidth - maxWidthMm)));
  }

  return nextColumns;
};

export const normalizePosSectionDraft = (section = {}, sectionKey = 'items') => {
  const limits = getSectionLimits(sectionKey);
  const safeX = clamp(section.xMm ?? THERMAL_LEFT_MARGIN_MM, limits.minX, limits.maxX);
  const maxWidthMm = getPosSectionMaxWidthMm(sectionKey, safeX);
  const normalizedColumns = normalizeColumnWidths(section.columns || [], maxWidthMm, limits.minColumnWidths);
  const titleYmm = clamp(section.titleYmm ?? 40, 8, 400);
  const tableYmm = Math.max(titleYmm + limits.minTitleGap, clamp(section.tableYmm ?? titleYmm + 4, 12, 420));

  return {
    ...section,
    xMm: safeX,
    titleYmm: round(titleYmm),
    tableYmm: round(tableYmm),
    tableHeightMm: round(clamp(section.tableHeightMm ?? 36, limits.minTableHeight, limits.maxTableHeight)),
    fontSize: round(clamp(section.fontSize ?? 6, limits.minFontSize, limits.maxFontSize)),
    columns: normalizedColumns,
  };
};

export const normalizePosStructureDraft = (draft = {}) => ({
  items: normalizePosSectionDraft(draft.items || {}, 'items'),
  tax: normalizePosSectionDraft(draft.tax || {}, 'tax'),
});

const createDefaultElements = () => {
  const itemsWidth = sumWidthsPt(POS_ITEMS_DEFAULT_COLUMNS);
  const taxWidth = sumWidthsPt(POS_TAX_DEFAULT_COLUMNS);
  return [
    {
      role: 'pos-items-title',
      type: 'text',
      text: '',
      x: mmToPt(3.53),
      y: mmToPt(40.92),
      width: itemsWidth,
      height: 0,
      fontSize: 7,
      fontWeight: 'bold',
      color: '#111827',
      zIndex: 20,
    },
    {
      role: 'pos-items-table',
      type: 'table',
      datasource: 'items',
      x: mmToPt(3.53),
      y: mmToPt(44.45),
      width: itemsWidth,
      height: mmToPt(46.57),
      fontSize: 5.5,
      headerColor: '#f5f5f4',
      borderColor: '#d6d3d1',
      columns: POS_ITEMS_DEFAULT_COLUMNS.map((column) => ({
        field: column.field,
        label: column.label,
        width: mmToPt(column.widthMm),
        align: column.align,
      })),
      zIndex: 21,
    },
    {
      role: 'pos-tax-title',
      type: 'text',
      text: 'Quadro Resumo de Impostos',
      x: mmToPt(3.53),
      y: mmToPt(93.84),
      width: taxWidth,
      height: 10,
      fontSize: 7,
      fontWeight: 'bold',
      color: '#111827',
      zIndex: 22,
    },
    {
      role: 'pos-tax-table',
      type: 'table',
      datasource: 'tax_summary_rows',
      x: mmToPt(3.53),
      y: mmToPt(97.37),
      width: taxWidth,
      height: mmToPt(16.93),
      fontSize: 6,
      headerColor: '#f5f5f4',
      borderColor: '#d6d3d1',
      columns: POS_TAX_DEFAULT_COLUMNS.map((column) => ({
        field: column.field,
        label: column.label,
        width: mmToPt(column.widthMm),
        align: column.align,
      })),
      zIndex: 23,
    },
  ];
};

const cloneLayout = (layout) => JSON.parse(JSON.stringify(layout || {}));

const findElementIndex = (elements, role, fallback) => elements.findIndex((element) => (
  element?.role === role || fallback(element)
));

const mergeColumns = (existingColumns = [], defaults = []) => defaults.map((fallbackColumn, index) => {
  const uniqueExistingColumns = (existingColumns || []).filter((column, existingIndex, source) => {
    if (!column?.field) return false;
    return source.findIndex((candidate) => candidate?.field === column.field) === existingIndex;
  });
  const existingColumn = uniqueExistingColumns.find((column) => column?.field === fallbackColumn.field) || uniqueExistingColumns[index] || {};
  return {
    field: fallbackColumn.field,
    label: fallbackColumn.label,
    align: existingColumn.align || fallbackColumn.align,
    widthMm: ptToMm(existingColumn.width || mmToPt(fallbackColumn.widthMm)),
  };
});

export const ensurePosStructureLayout = (layout) => {
  const nextLayout = cloneLayout(layout);
  nextLayout.canvasWidth = nextLayout.canvasWidth || THERMAL_WIDTH_PT;
  nextLayout.isThermal = true;
  nextLayout.autoHeight = true;
  nextLayout.elements = Array.isArray(nextLayout.elements) ? [...nextLayout.elements] : [];

  const defaults = createDefaultElements();
  defaults.forEach((defaultElement) => {
    const elementIndex = findElementIndex(nextLayout.elements, defaultElement.role, (element) => {
      if (!element || element.type !== defaultElement.type) return false;
      if (defaultElement.type === 'table') {
        return element.datasource === defaultElement.datasource;
      }
      return element.text === defaultElement.text;
    });

    if (elementIndex === -1) {
      nextLayout.elements.push(defaultElement);
      return;
    }

    const currentElement = nextLayout.elements[elementIndex] || {};
    const merged = {
      ...defaultElement,
      ...currentElement,
      role: defaultElement.role,
      text: defaultElement.text,
    };

    if (defaultElement.type === 'table') {
      const defaultColumns = defaultElement.role === 'pos-items-table' ? POS_ITEMS_DEFAULT_COLUMNS : POS_TAX_DEFAULT_COLUMNS;
      const mergedColumns = mergeColumns(currentElement.columns, defaultColumns);
      merged.columns = mergedColumns.map((column) => ({
        field: column.field,
        label: column.label,
        align: column.align,
        width: mmToPt(column.widthMm),
      }));
      merged.datasource = defaultElement.datasource;
    }

    nextLayout.elements[elementIndex] = merged;
  });

  const uniquePosRoles = new Set();
  nextLayout.elements = nextLayout.elements.filter((element) => {
    const role = element?.role;
    if (!['pos-items-title', 'pos-items-table', 'pos-tax-title', 'pos-tax-table'].includes(role)) {
      return true;
    }
    if (uniquePosRoles.has(role)) {
      return false;
    }
    uniquePosRoles.add(role);
    return true;
  });

  nextLayout.elements = nextLayout.elements.sort((left, right) => (left?.zIndex || 0) - (right?.zIndex || 0));
  return nextLayout;
};

const buildSectionState = (titleElement, tableElement, defaultColumns) => {
  const mergedColumns = mergeColumns(tableElement?.columns, defaultColumns);
  return {
    xMm: ptToMm(tableElement?.x),
    titleYmm: ptToMm(titleElement?.y),
    tableYmm: ptToMm(tableElement?.y),
    tableHeightMm: ptToMm(tableElement?.height),
    fontSize: Number(tableElement?.fontSize || 6),
    columns: mergedColumns,
  };
};

export const extractPosStructureDraft = (layout) => {
  const ensuredLayout = ensurePosStructureLayout(layout);
  const elements = ensuredLayout.elements || [];
  const itemsTitle = elements.find((element) => element.role === 'pos-items-title');
  const itemsTable = elements.find((element) => element.role === 'pos-items-table');
  const taxTitle = elements.find((element) => element.role === 'pos-tax-title');
  const taxTable = elements.find((element) => element.role === 'pos-tax-table');

  return normalizePosStructureDraft({
    items: buildSectionState(itemsTitle, itemsTable, POS_ITEMS_DEFAULT_COLUMNS),
    tax: buildSectionState(taxTitle, taxTable, POS_TAX_DEFAULT_COLUMNS),
  });
};

const applySection = (elements, roleTitle, roleTable, section) => {
  const titleIndex = elements.findIndex((element) => element.role === roleTitle);
  const tableIndex = elements.findIndex((element) => element.role === roleTable);
  if (titleIndex === -1 || tableIndex === -1) return;

  const widthPt = sumWidthsPt(section.columns);
  elements[titleIndex] = {
    ...elements[titleIndex],
    x: mmToPt(section.xMm),
    y: mmToPt(section.titleYmm),
    width: widthPt,
    fontSize: Number(section.fontSize || elements[titleIndex].fontSize || 7),
  };

  elements[tableIndex] = {
    ...elements[tableIndex],
    x: mmToPt(section.xMm),
    y: mmToPt(section.tableYmm),
    width: widthPt,
    height: mmToPt(section.tableHeightMm),
    fontSize: Number(section.fontSize || elements[tableIndex].fontSize || 6),
    columns: section.columns.map((column) => ({
      field: column.field,
      label: column.label,
      align: column.align,
      width: mmToPt(column.widthMm),
    })),
  };
};

export const applyPosStructureDraft = (layout, draft) => {
  const nextLayout = ensurePosStructureLayout(layout);
  const nextElements = [...(nextLayout.elements || [])];
  const normalizedDraft = normalizePosStructureDraft(draft);
  applySection(nextElements, 'pos-items-title', 'pos-items-table', normalizedDraft.items);
  applySection(nextElements, 'pos-tax-title', 'pos-tax-table', normalizedDraft.tax);
  nextLayout.elements = nextElements;
  const bottomEdge = nextElements.reduce((maximum, element) => {
    const y = Number(element?.y || 0);
    const height = Number(element?.height || 0);
    return Math.max(maximum, y + height);
  }, 0);
  nextLayout.canvasHeight = Math.max(Number(nextLayout.canvasHeight || 0), round(bottomEdge + 20));
  return nextLayout;
};

export const getSectionWidthMm = (section = {}) => round((section.columns || []).reduce((total, column) => total + Number(column.widthMm || 0), 0));